import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';
import {
  IHbciAccount,
  IHbciAccountCreate,
  IHbciAccountUpdate,
  IHbciDecoupledStatus,
  IHbciRefreshTanRequired,
  IHbciTransactionsResult,
} from '../../Interfaces/IHbciAccount';

type IHbciRefreshResult = IHbciAccount | IHbciRefreshTanRequired;

const QK = (idLegalTransaction: number) => ['hbciaccount', idLegalTransaction];

export const useGetHbciAccounts = (idLegalTransaction: number | null) => {
  const enabled = idLegalTransaction !== null && idLegalTransaction > 0;
  const {
    data: hbciAccounts,
    isLoading: isLoadingHbciAccounts,
    isError: isErrorHbciAccounts,
    refetch: refetchHbciAccounts,
  } = useQuery<IHbciAccount[]>({
    queryKey: QK(idLegalTransaction ?? 0),
    queryFn: () =>
      customFetcher({
        endpoint: `/hbciaccount?idLegalTransaction=${idLegalTransaction}`,
      }),
    enabled,
  });
  return {
    hbciAccounts: hbciAccounts ?? [],
    isLoadingHbciAccounts,
    // Fehlgeschlagener Abruf (z. B. 403, weil die Erweiterung nicht aktiv ist)
    // muss von "Konto existiert nicht mehr" unterscheidbar bleiben — sonst
    // behauptet die Verfolgungs-Zeile, ein Konto sei gelöscht.
    isErrorHbciAccounts,
    refetchHbciAccounts,
  };
};

export const useCreateHbciAccount = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: createHbciAccount,
    isPending: isPendingCreateHbciAccount,
  } = useMutation({
    mutationFn: (body: IHbciAccountCreate) =>
      customFetcher({ endpoint: '/hbciaccount', method: 'POST', body }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: QK(variables.LegalTransaction_idLegalTransaction),
      });
    },
  });
  return { createHbciAccount, isPendingCreateHbciAccount };
};

export const useUpdateHbciAccount = (idLegalTransaction: number) => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: updateHbciAccount,
    isPending: isPendingUpdateHbciAccount,
  } = useMutation({
    mutationFn: ({ id, body }: { id: number; body: IHbciAccountUpdate }) =>
      customFetcher({ endpoint: `/hbciaccount/${id}`, method: 'PUT', body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK(idLegalTransaction) });
    },
  });
  return { updateHbciAccount, isPendingUpdateHbciAccount };
};

export const useDeleteHbciAccount = (idLegalTransaction: number) => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: deleteHbciAccount,
    isPending: isPendingDeleteHbciAccount,
  } = useMutation({
    mutationFn: (id: number) =>
      customFetcher({ endpoint: `/hbciaccount/${id}`, method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK(idLegalTransaction) });
    },
  });
  return { deleteHbciAccount, isPendingDeleteHbciAccount };
};

export const useRefreshHbciAccount = (idLegalTransaction: number) => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: refreshHbciAccount,
    isPending: isPendingRefreshHbciAccount,
  } = useMutation<IHbciRefreshResult, Error, number>({
    mutationFn: (id: number) =>
      customFetcher({
        endpoint: `/hbciaccount/${id}/refresh`,
        method: 'POST',
      }) as Promise<IHbciRefreshResult>,
    onSuccess: result => {
      // Bei TAN-Bedarf NICHT invalidieren — der Saldo hat sich nicht
      // geaendert, das passiert erst nach erfolgreicher TAN-Bestaetigung.
      if ((result as IHbciRefreshTanRequired).tanRequired) return;
      queryClient.invalidateQueries({ queryKey: QK(idLegalTransaction) });
    },
  });
  return { refreshHbciAccount, isPendingRefreshHbciAccount };
};

/**
 * Offene TAN-Challenge abschliessen — User hat die TAN ihrer Bank-App
 * eingegeben. Antwort: entweder das aktualisierte Konto (Saldo) oder
 * eine weitere Challenge (mehrstufige Verfahren).
 */
export const useCompleteHbciTanChallenge = (idLegalTransaction: number) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation<
    IHbciRefreshResult,
    Error,
    { idHbciAccount: number; idHbciTanChallenge: number; tan: string }
  >({
    mutationFn: ({ idHbciAccount, idHbciTanChallenge, tan }) =>
      customFetcher({
        endpoint: `/hbciaccount/${idHbciAccount}/refresh/tan`,
        method: 'POST',
        body: { idHbciTanChallenge, tan },
      }) as Promise<IHbciRefreshResult>,
    onSuccess: result => {
      if ((result as IHbciRefreshTanRequired).tanRequired) return;
      queryClient.invalidateQueries({ queryKey: QK(idLegalTransaction) });
    },
  });
  return {
    completeHbciTanChallenge: mutateAsync,
    isPendingCompleteHbciTanChallenge: isPending,
  };
};

/**
 * Manueller HKKAZ-Abruf — Kontoumsaetze on-demand. Wird vom
 * Notaranderkonto-Buchungen-Dialog aufgerufen. Anders als refresh wird
 * das Ergebnis nicht in DiNo persistiert; der Aufruf liefert die Liste
 * direkt zurueck.
 *
 * useMutation statt useQuery, weil Aufruf bewusst und mit Parametern
 * geschieht (Zeitraum) und es eine ``tanRequired``-Variante geben kann.
 */
type IHbciTransactionsApiResult =
  | IHbciTransactionsResult
  | IHbciRefreshTanRequired;

export const useFetchHbciAccountTransactions = () => {
  const { mutateAsync, isPending } = useMutation<
    IHbciTransactionsApiResult,
    Error,
    { idHbciAccount: number; fromDate?: string; toDate?: string }
  >({
    mutationFn: ({ idHbciAccount, fromDate, toDate }) => {
      const params: string[] = [];
      if (fromDate) params.push(`fromDate=${encodeURIComponent(fromDate)}`);
      if (toDate) params.push(`toDate=${encodeURIComponent(toDate)}`);
      const qs = params.length ? `?${params.join('&')}` : '';
      return customFetcher({
        endpoint: `/hbciaccount/${idHbciAccount}/transactions${qs}`,
      }) as Promise<IHbciTransactionsApiResult>;
    },
  });
  return {
    fetchHbciAccountTransactions: mutateAsync,
    isFetchingHbciAccountTransactions: isPending,
  };
};

/**
 * Polling fuer decoupled-Verfahren (ING-App, pushTAN bei Sparkassen).
 * Wird vom Dialog im Intervall aufgerufen, bis ``status==='confirmed'``.
 */
export const usePollHbciDecoupled = (idLegalTransaction: number) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation<
    IHbciDecoupledStatus,
    Error,
    { idHbciAccount: number; idHbciTanChallenge: number }
  >({
    mutationFn: ({ idHbciAccount, idHbciTanChallenge }) =>
      customFetcher({
        endpoint: `/hbciaccount/${idHbciAccount}/refresh/decoupled-status`,
        method: 'POST',
        body: { idHbciTanChallenge },
      }) as Promise<IHbciDecoupledStatus>,
    onSuccess: result => {
      if (result.status === 'confirmed') {
        queryClient.invalidateQueries({ queryKey: QK(idLegalTransaction) });
      }
    },
  });
  return {
    pollHbciDecoupled: mutateAsync,
    isPendingPollHbciDecoupled: isPending,
  };
};
